/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

type HorizontalScrollProps = {
    withArrow?: boolean;
    fadeColor?: string;
    className?: string;
    children: ReactNode;
}

export const HorizontalScroll = ({ withArrow, fadeColor = "#fff", className, children }: HorizontalScrollProps) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const [isAtRight, setIsAtRight] = useState(false);
    const [isAtLeft, setIsAtLeft] = useState(false);

    const el = scrollContainerRef.current;
    const hasFade = !!el && el.scrollWidth > el.clientWidth;
    const height = !!el && el.clientHeight;

    const handleScroll = () => {
        const el = scrollContainerRef.current;

        if (!el) return;

        const { scrollLeft, scrollWidth, clientWidth } = el;

        setIsAtLeft(scrollLeft === 0);
        setIsAtRight(Math.ceil(scrollLeft + clientWidth) === scrollWidth);
    };

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        const el = scrollContainerRef.current;

        if (el && hasFade) {
            handleScroll(); // Check initially
            el.addEventListener("scroll", handleScroll);

            return () => {
                el.removeEventListener("scroll", handleScroll);
            };
        }
    }, [hasFade]);

    const wrapperStyles = useMemo(() => {
        return css`
        position: relative;

        &:after {
            right: 0;
            background: linear-gradient(90deg, transparent, ${fadeColor});
            opacity: ${isAtRight ? "0" : "1"};
        }

        &:before {
            left: 0;
            background: linear-gradient(90deg, ${fadeColor}, transparent);
            opacity: ${isAtLeft ? "0" : "1"};
        }

        &:before, &:after {
            content: "";
            position: absolute;
            top: 0;
            height: ${height}px;
            width: 50px;
            transition: opacity 0.3s ease-in-out;
            pointer-events: none;
        }
    `;
    }, [fadeColor, isAtLeft, isAtRight, height]);

    return (
        <div className="w-100 d-flex" css={hasFade && wrapperStyles}>
            {withArrow && hasFade && !isAtRight && (
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className="text-muted position-absolute translate-middle-y"
                    css={css`z-index: 10; top: ${(height || 0) / 2}px; right: 4px;`}
                />
            )}

            <div className={`d-flex w-100 gap-2 overflow-x-auto ${className}`} ref={scrollContainerRef}>
                {children}
            </div>
        </div>
    );
};
